import { SERVICE_TYPES, SHIPPING_ESTIMATE_BUFFER_DAYS } from '@/constants/FullfilmentTypes';

import { CartItem } from '@/client/services/vendor/models';
import { FulfillmentProfile, OfferContent } from '@/server/services/vendor/models';
import { addDays, format } from 'date-fns-jalali';

type OfferFulfillmentSource = Pick<OfferContent, 'fulfillmentProfiles' | 'hasShipping' | 'hasInstallCenter' | 'hasInstallOnsite' | 'hasPickupCenter'>;

type FulfillmentClockTime = {
    hour: number;
    minute: number;
    second?: number;
    nano?: number;
};

type FulfillmentTimeValue = string | FulfillmentClockTime | null | undefined;

export const isShippingFulfillment = (fulfillmentType = '') => fulfillmentType.toUpperCase().includes('SHIPPING');

export type ShippingDateRangeParts = {
    startDay: string;
    endDay: string;
    startMonth: string;
    endMonth: string;
    sameMonth: boolean;
};

/**
 * Shipping delivery range parts, from the estimated date to the estimated date plus the buffer.
 */
export const getShippingDateRangeParts = (estimatedDate: string | Date): ShippingDateRangeParts => {
    const start = new Date(estimatedDate);
    const end = addDays(start, SHIPPING_ESTIMATE_BUFFER_DAYS);
    const startDay = format(start, 'd');
    const startMonth = format(start, 'MMMM');
    const endDay = format(end, 'd');
    const endMonth = format(end, 'MMMM');

    return {
        startDay,
        endDay,
        startMonth,
        endMonth,
        sameMonth: startMonth === endMonth
    };
};

/**
 * Shipping delivery range, from the estimated date to the estimated date plus the buffer.
 * Same month: "22 تا 27 مهر"; different months: "28 مهر تا 3 آبان".
 */
export const formatShippingDateRange = (estimatedDate: string | Date) => {
    const { startDay, endDay, startMonth, endMonth, sameMonth } = getShippingDateRangeParts(estimatedDate);

    if (!sameMonth) {
        return `${startDay} ${startMonth} تا ${endDay} ${endMonth}`;
    }

    return `${startDay} تا ${endDay} ${endMonth}`;
};

export const isInstallCenterFulfillment = (fulfillmentType = '') => fulfillmentType.toUpperCase().includes('INSTALL_CENTER');

export const isPickupCenterFulfillment = (fulfillmentType = '') => fulfillmentType.toUpperCase().includes('PICKUP_CENTER');

export const isStoreVisitFulfillment = (fulfillmentType = '') =>
    isInstallCenterFulfillment(fulfillmentType) || isPickupCenterFulfillment(fulfillmentType);

export const isInstallOnsiteFulfillment = (fulfillmentType = '') => fulfillmentType.toUpperCase().includes('INSTALL_ONSITE');

/** Shipping and pickup have no install labor fee. */
export const isZeroLaborFeeFulfillment = (fulfillmentType = '') =>
    isShippingFulfillment(fulfillmentType) || isPickupCenterFulfillment(fulfillmentType);

export const resolveFulfillmentServiceType = (fulfillmentType = '') => {
    const type = fulfillmentType.toUpperCase();

    if (type.includes('PICKUP_CENTER')) return SERVICE_TYPES.PICKUP_CENTER;
    if (type.includes('INSTALL_CENTER')) return SERVICE_TYPES.INSTALL_CENTER;
    if (type.includes('INSTALL_ONSITE')) return SERVICE_TYPES.INSTALL_ONSITE;

    return SERVICE_TYPES.SHIPPING;
};

export const getCartItemBookingUuid = (item: Pick<CartItem, 'params' | 'fulfillmentBookingUuid'>) =>
    item.params?.bookingUuid || item.fulfillmentBookingUuid || null;

export const isCartItemFulfillmentReady = (item: CartItem) => Boolean(item.fulfillmentDate && getCartItemBookingUuid(item));

export const formatFulfillmentTimeValue = (time: FulfillmentTimeValue) => {
    if (!time) {
        return null;
    }

    if (typeof time === 'string') {
        return time.slice(0, 5);
    }

    if (typeof time === 'object' && typeof time.hour === 'number' && typeof time.minute === 'number') {
        return `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`;
    }

    return null;
};

export const getFulfillmentLabel = (fulfillmentType = '') => resolveFulfillmentServiceType(fulfillmentType).label;

export const getFulfillmentIcon = (fulfillmentType = '') => resolveFulfillmentServiceType(fulfillmentType).icon;

export const getEnabledFulfillmentProfiles = (profiles?: FulfillmentProfile[] | null): FulfillmentProfile[] =>
    profiles?.filter((profile) => !SERVICE_TYPES[profile.fulfillmentType?.toUpperCase() as keyof typeof SERVICE_TYPES]?.disabled) ?? [];

export const resolveOfferFulfillmentProfiles = (offer?: OfferFulfillmentSource | null): FulfillmentProfile[] =>
    getEnabledFulfillmentProfiles(offer?.fulfillmentProfiles);

export const getDefaultFulfillmentProfile = (offer?: OfferFulfillmentSource | null): FulfillmentProfile | null =>
    resolveOfferFulfillmentProfiles(offer).find((profile) => profile.isEligible === true) ?? null;
